import {
  Card,
  CardContent,
  Typography,
  TextField,
  Box,
  InputAdornment,
  Chip,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import SearchIcon from "@mui/icons-material/Search";
import NoDataFound from "../common/NoDataFound";
import dayjs from "dayjs";
import { fetchUserActivityDetails } from "../../services/dashboard.service";
import { useFilterContext } from "../../usageandbilling/FilterContext";

const CustomNoRowsOverlay = () => (
  <NoDataFound
    title="No data found"
    subtitle="Try adjusting your search criteria or check back later."
    imageWidth={150}
    imageHeight={150}
  />
);

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// Feature badge component with tooltip
const FeatureBadge = ({
  label,
  value,
  color,
  tooltip,
}: {
  label: string;
  value: number | string;
  color: string;
  tooltip?: string;
}) => {
  if (!value || value === 0 || value === "0m") return null;

  const badge = (
    <Chip
      label={`${label}: ${value}`}
      size="small"
      sx={{
        fontSize: "0.75rem",
        height: "24px",
        backgroundColor: color,
        color: "white",
        fontWeight: 600,
        "& .MuiChip-label": {
          px: 1.5,
        },
        cursor: tooltip ? "help" : "default",
      }}
    />
  );

  if (tooltip) {
    return (
      <Box component="span" sx={{ display: "inline-block" }}>
        <Tooltip title={tooltip} arrow placement="top">
          {badge}
        </Tooltip>
      </Box>
    );
  }

  return badge;
};

// Render feature badges in a compact grid
const renderFeatureBadges = (params: GridRenderCellParams) => {
  const row = params.row;
  const features = [
    {
      label: "Transcriptions",
      value: row.transcriptions,
      color: "#1976d2",
      tooltip: "Number of audio/video transcriptions created",
    },
    {
      label: "Transcript Duration",
      value: row.transcriptionDurationHours
        ? formatDuration(row.transcriptionDurationHours * 3600)
        : 0,
      color: "#0288d1",
      tooltip: "Total duration of transcribed content",
    },
    {
      label: "Fact Finds",
      value: row.factFinds,
      color: "#2e7d32",
      tooltip: "Number of fact find forms completed",
    },
    {
      label: "Meetings",
      value: row.meetings,
      color: "#ed6c02",
      tooltip: "Number of manual meetings scheduled or created",
    },
    {
      label: "Calendly",
      value: row.calendlyMeetings,
      color: "#00a2ff",
      tooltip: "Number of Calendly meetings scheduled",
    },
    {
      label: "Clients Created",
      value: row.clients,
      color: "#9c27b0",
      tooltip: "Number of new client records created",
    },
    {
      label: "Clients Assigned",
      value: row.clientsAssigned,
      color: "#7b1fa2",
      tooltip: "Number of clients assigned to this advisor",
    },
    {
      label: "Documents",
      value: row.documents,
      color: "#0288d1",
      tooltip: "Number of documents uploaded",
    },
    {
      label: "AI Agent",
      value: row.aiAgentUsage,
      color: "#d32f2f",
      tooltip: "Number of AI agent interactions",
    },
    {
      label: "Contacts",
      value: row.contacts,
      color: "#5e35b1",
      tooltip: "Number of contact records created",
    },
    {
      label: "File Notes",
      value: row.fileNotesGenerated,
      color: "#388e3c",
      tooltip: "Number of file notes generated",
    },
    {
      label: "Note Regens",
      value: row.fileNotesRegenerated,
      color: "#689f38",
      tooltip: "Number of file notes regenerated",
    },
    {
      label: "PowerPoints",
      value: row.powerPointsGenerated,
      color: "#f57c00",
      tooltip: "Number of PowerPoint presentations generated",
    },
    {
      label: "Emails Generated",
      value: row.emailsGenerated,
      color: "#5e35b1",
      tooltip: "Number of emails generated by AI",
    },
    {
      label: "Emails Sent",
      value: row.emailsSent,
      color: "#1565c0",
      tooltip: "Number of emails actually sent",
    },
    {
      label: "SMS Sent",
      value: row.smsSent,
      color: "#ff5722",
      tooltip: "Number of SMS messages sent",
    },
    {
      label: "SMS Segments",
      value: row.smsSegments,
      color: "#e64a19",
      tooltip: "Total SMS segments used (for billing purposes)",
    },
  ];

  const usedFeatures = features.filter(
    (f) => f.value && f.value !== 0 && f.value !== "0m",
  );

  if (usedFeatures.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        No activity
      </Typography>
    );
  }

  return (
    <Box display="flex" flexWrap="wrap" gap={0.75} py={1}>
      {usedFeatures.map((feature, index) => (
        <FeatureBadge
          key={index}
          label={feature.label}
          value={feature.value}
          color={feature.color}
          tooltip={feature.tooltip}
        />
      ))}
    </Box>
  );
};

const columns: GridColDef[] = [
  {
    field: "name",
    headerName: "NAME",
    width: 180,
    disableColumnMenu: true,
    sortable: true,
    filterable: false,
  },
  {
    field: "email",
    headerName: "EMAIL",
    width: 220,
    disableColumnMenu: true,
    sortable: true,
    filterable: false,
  },
  {
    field: "totalSessions",
    headerName: "SESSIONS",
    type: "number",
    width: 120, // Increased width
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    sortable: true,
    filterable: false,
  },
  {
    field: "averageDuration",
    headerName: "AVG DURATION",
    width: 140, // Increased width
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    sortable: true,
    filterable: false,
    renderCell: (params) => (params.value ? formatDuration(params.value) : "—"),
  },
  {
    field: "featureUsage",
    headerName: "FEATURE USAGE",
    flex: 1,
    minWidth: 400,
    disableColumnMenu: true,
    sortable: false,
    filterable: false,
    renderCell: renderFeatureBadges,
  },
  {
    field: "lastActivity",
    headerName: "LAST ACTIVITY",
    width: 180, // Increased width for Date + Time + Sort Arrow
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    sortable: true,
    filterable: false,
    renderCell: (params) =>
      // Added HH:mm for time
      params.value ? dayjs(params.value).format("YYYY-MM-DD HH:mm:ss") : "—",
  },
];

interface UsersTableProps {
  onRowClick?: (userId: string, username: string) => void;
}

const UsersTable = ({ onRowClick }: UsersTableProps) => {
  const { selectedOrgIds, selectedUserIds, dateRange } = useFilterContext();
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState<any[]>([
    { field: "lastActivity", sort: "desc" },
  ]);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const timeoutId = setTimeout(async () => {
      if (!dateRange[0] || !dateRange[1]) return;

      setLoading(true);

      try {
        const params = new URLSearchParams({
          startTime: dateRange[0].startOf("day").toISOString(),
          endTime: dateRange[1].endOf("day").toISOString(),
          page: String(paginationModel.page + 1),
          limit: String(paginationModel.pageSize),
        });

        if (selectedOrgIds.length > 0) params.set("orgIds", selectedOrgIds.join(","));
        if (selectedUserIds.length > 0) params.set("userIds", selectedUserIds.join(","));
        if (searchText) params.append("search", searchText);

        if (sortModel.length > 0) {
          params.append("sortBy", sortModel[0].field);
          params.append("sortOrder", sortModel[0].sort);
        }

        const response: any = await fetchUserActivityDetails(params.toString());

        // Add id field for DataGrid
        const rowsWithId = response.data.map((row: any, index: number) => ({
          ...row,
          id: row.userId || `row-${index}`,
        }));

        setRows(rowsWithId);
        setRowCount(response.pagination.total);
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        console.error("Failed to fetch user activity data:", err);
        setRows([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    }, searchText ? 500 : 0);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [dateRange, selectedOrgIds, selectedUserIds, searchText, paginationModel, sortModel]);

  return (
    <Card sx={{ width: "100%" }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          ml={1}
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            User Activity Details
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by name or email"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
              },
              width: "300px",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            sortingMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel([...model])}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnResize={false}
            disableColumnMenu={false}
            onRowClick={(params) => {
              console.log(
                "[UsersTable] Row clicked:",
                params.row.userId,
                params.row.name,
              );
              if (onRowClick) {
                onRowClick(params.row.userId, params.row.name);
              }
            }}
            getRowHeight={() => "auto"}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-cell": {
                py: 1,
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
              "& .MuiDataGrid-row": {
                cursor: onRowClick ? "pointer" : "default",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: onRowClick
                  ? "rgba(0, 0, 0, 0.04)"
                  : "transparent",
              },
              // Align pagination controls to the right (default MUI behavior is left/space-between)
              "& .MuiDataGrid-footerContainer": {
                justifyContent: "flex-end",
                alignItems: "center",
                // ensure pagination contents don't wrap awkwardly on small widths
                flexWrap: "nowrap",
                padding: "8px",
              },
              // Ensure the underlying TablePagination aligns its contents to the right
              "& .MuiTablePagination-root": {
                justifyContent: "flex-end",
                alignItems: "center",
              },
              // Keep toolbar items aligned to the right and preserve normal spacing
              "& .MuiTablePagination-toolbar": {
                justifyContent: "flex-end",
                gap: "8px",
                paddingLeft: 0,
                paddingRight: 0,
                width: "100%",
              },
              // Make sure the rows-per-page label and select have normal spacing
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                margin: 0,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default UsersTable;
